export type ProfileMilestone = 'profile_content_ready' | 'booking_cta_ready';
export type ProfileReadinessMetric = {
  milestone: ProfileMilestone;
  elapsed_ms: number;
  transition_id: string;
  from_route: string;
  to_route: '/dr/[slug]';
};

/** Local-only slug matches the mounted page; never included in a telemetry payload. */
export function watchProfileReadiness(
  navigation: { slug: string; startedAt: number; transitionId: string; fromRoute: string },
  send: (metric: ProfileReadinessMetric) => void,
): (() => void) & { routeComplete: () => void } {
  let stopped = false;
  let firstFrame = 0;
  let secondFrame = 0;
  const reported = new Set<ProfileMilestone>();
  const previousRoot = [...document.querySelectorAll<HTMLElement>('main[data-profile-rum-slug]')].find(main => main.dataset.profileRumSlug === navigation.slug);
  let completed = false;
  const visible = (element: HTMLElement) => {
    if (element.closest('[aria-hidden="true"], [hidden], [inert], [aria-busy="true"]')) return false;
    const style = getComputedStyle(element);
    const box = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && style.pointerEvents !== 'none' && box.width > 0 && box.height > 0;
  };
  const ready = () => {
    if (stopped || document.visibilityState !== 'visible') return [];
    const root = [...document.querySelectorAll<HTMLElement>('main[data-profile-rum-slug]')]
      .find(main => main.dataset.profileRumSlug === navigation.slug && (completed || main !== previousRoot) && visible(main));
    if (!root) return [];
    const milestones: ProfileMilestone[] = [];
    if (root.dataset.profileContentReady === 'true' && root.querySelector('h1')?.textContent?.trim()) milestones.push('profile_content_ready');
    const button = root.dataset.profileInteractive === 'false' ? undefined : [...root.querySelectorAll<HTMLElement>('[data-profile-booking-cta]')].find(element => {
      if ((element as HTMLButtonElement).disabled || element.getAttribute('aria-disabled') === 'true' || !visible(element)) return false;
      const box = element.getBoundingClientRect();
      // Hit-test an on-screen CTA so a navigation overlay never counts as readiness.
      // Off-screen CTAs are available by scrolling; no scroll is performed by monitoring.
      if (box.bottom > 0 && box.top < innerHeight && box.right > 0 && box.left < innerWidth) {
        const hit = document.elementFromPoint(Math.min(innerWidth - 1, Math.max(0, box.left + box.width / 2)), Math.min(innerHeight - 1, Math.max(0, box.top + box.height / 2)));
        if (!hit || !element.contains(hit)) return false;
      }
      return true;
    });
    if (button) milestones.push('booking_cta_ready');
    return milestones;
  };
  const check = () => {
    firstFrame = 0;
    if (!ready().some(m => !reported.has(m))) return;
    // Two frames give the commit a paint opportunity; this remains a paint approximation.
    secondFrame = requestAnimationFrame(() => {
      secondFrame = 0;
      for (const milestone of ready()) {
        if (reported.has(milestone)) continue;
        reported.add(milestone);
        send({ milestone, elapsed_ms: Math.round(performance.now() - navigation.startedAt), transition_id: navigation.transitionId, from_route: navigation.fromRoute, to_route: '/dr/[slug]' });
      }
      if (reported.size === 2) stop();
    });
  };
  const schedule = () => { if (!stopped && !firstFrame && !secondFrame) firstFrame = requestAnimationFrame(check); };
  const observer = new MutationObserver(schedule);
  const timeout = setTimeout(() => stop(), 30_000);
  const stop = () => {
    stopped = true;
    observer.disconnect();
    cancelAnimationFrame(firstFrame); cancelAnimationFrame(secondFrame);
    clearTimeout(timeout);
    document.removeEventListener('visibilitychange', schedule);
    window.removeEventListener('scroll', schedule, true);
    window.removeEventListener('resize', schedule);
  };
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-profile-content-ready', 'data-profile-interactive', 'data-profile-rum-slug', 'disabled', 'aria-disabled', 'aria-busy', 'aria-hidden', 'hidden', 'inert', 'class', 'style'] });
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-route-dir'] });
  document.addEventListener('visibilitychange', schedule);
  window.addEventListener('scroll', schedule, { capture: true, passive: true });
  window.addEventListener('resize', schedule);
  schedule();
  return Object.assign(stop, { routeComplete: () => { completed = true; schedule(); } });
}
